import * as Dfns from 'date-fns/fp';
import { evaluate } from 'mathjs';
import * as R from 'ramda';
import { Dispatch, ReactElement, SetStateAction, useCallback, useMemo } from 'react';
import { OverlayTriggerProps } from 'react-bootstrap';
import * as uuid from 'uuid';
import {
  ColumnOrMetricName,
  DimensionColNames,
  DimensionColPrettyNames
} from '@blisspointmedia/bpm-types/dist/Columns';
import * as CC from '@blisspointmedia/bpm-types/dist/CrossChannelPerformance';
import { resolveHigherIsBetter } from '@blisspointmedia/bpm-types/dist/dashboarding';
import { FilterPaneState } from '@blisspointmedia/bpm-types/dist/FilterPane';
import {
  Formatter,
  toPrettyNumber,
  toPrettyPercent
} from '@blisspointmedia/bpm-types/dist/formatUtils';
import { KpiInfo } from '@blisspointmedia/bpm-types/dist/Kpis';
import * as L from '@blisspointmedia/bpm-types/dist/LinearPerformance';
import {
  CPX_FORMATTER,
  IMP_IN_THOUSANDS_FORMATTER,
  SPEND_OR_REVENUE_FORMATTER,
  toPrettyDeltaPercent
} from '@blisspointmedia/bpm-types/dist/metricFormatUtils';
import { AggregatorDatum } from '@blisspointmedia/bpm-types/dist/Metrics';
import {
  Column,
  DimensionColumn,
  HeatMapData,
  MetricsTablePreset,
  PerformanceData,
  SuperHeader
} from '@blisspointmedia/bpm-types/dist/MetricsTable';
import { PerformanceDataRow, SortInfo } from '@blisspointmedia/bpm-types/dist/Performance';
import {
  CustomMetric,
  CustomMetricKPI
} from '@blisspointmedia/bpm-types/dist/pgTables/CustomMetric';
import { DATE_FORMAT } from '@blisspointmedia/bpm-types/dist/RelativeDatePicker';
import { COMMERCE } from '@blisspointmedia/bpm-types/dist/ScpChannels';
import { GroupedSegment } from '@blisspointmedia/bpm-types/dist/SegmentationMapping';
import * as S from '@blisspointmedia/bpm-types/dist/StreamingPerformance';
import { CDN } from '@src/utils/company-logo-constants';
import {
  DivergentColorMap20,
  DivergentGreenDark,
  getSteppedHeatColor,
  SequentialGreenRamp
} from '../../utils/colors';
import { isServerValueUsable, readServerValue, ServerMetricValues } from './server-metric-values';

// Both ramps are authored min -> max for a column where a HIGHER value is the better one. A
// min-is-best column reads them backwards. Hoisted rather than reversed inline because
// `getHeatMapInfo` runs once per cell.
const DIVERGENT_STOPS_MIN_IS_BEST = [...DivergentColorMap20].reverse();
const SEQUENTIAL_STOPS_MIN_IS_BEST = [...SequentialGreenRamp].reverse();

// Mathematical operators
export const OPERATORS = ['+', '-', '*', '/', '^'];

// Math functions (without parentheses)
export const FUNCTIONS = ['sqrt', 'log', 'abs'];

export interface CellData {
  backgroundColor?: string;
  bold?: boolean;
  content?: ReactElement; // Pass in custom elements
  label: string;
  overlayText?: string;
  textColor?: string;
  url?: string;
  value: number | string;
  adInfo?: AdInfoRow;
}

export interface SuperHeaderItem {
  leftDivider: boolean;
  rightDivider: boolean;
  text: string;
}

export interface AdInfoRow {
  company: string;
  platform: string;
  ad_name: string;
  ad_id: string;
  account_id: string;
}

/** True for a usable value: not null, undefined, NaN or ±Infinity. */
export const hasNumber = (val: unknown): val is number =>
  typeof val === 'number' && Number.isFinite(val);

export { CDN };

export const getCreativeAsset = (company: string, file: string): string =>
  `${CDN}/creativeAssets/assets/${company}/${file}`;
export const getCreativeThumbnail = (company: string, isci: string): string =>
  `${CDN}/creativeAssets/thumbnails/${company}/${isci}.png`;

// Values match the kebab-case CSS classes in MetricsTable.scss (`.inner-cell.text.{value}`).
// Prefixed with `metrics-text-` so they don't collide with any generic `.regular`/
// `.small`/`.extra-small` class a child component might use inside `.metrics-table`.
export const TEXT_SIZE_MAP = {
  Metrics: 'metrics-text-regular',
  SparkChart: 'metrics-text-extra-small',
  Widget: 'metrics-text-small'
};
export const TEXT_FONT_SIZE_MAP = {
  Metrics: 14,
  SparkChart: 12,
  Widget: 12
};

export type DimensionMap =
  | Record<L.Dimension, string>
  | Record<S.PerformanceDimension, string>
  | Record<CC.Dimension, string>
  // can replace above scp Dimension types
  | Record<DimensionColNames, string>;

export const DEFAULT_GET_DIMENSION_CELL = (
  dimensionData: DimensionMap,
  dimensionHeader: DimensionColumn
): CellData => {
  const value = dimensionData[dimensionHeader.dimensionVarName];
  return {
    label: value,
    value
  };
};

type ALL_DIMS =
  | 'Date'
  | CC.Dimension
  | L.Dimension
  | S.PerformanceDimension
  // for SCP refactor can replace other scp after we migrate
  | DimensionColNames
  | DimensionColPrettyNames
  | ColumnOrMetricName;

export interface ColumnMetaData<DIMS = ALL_DIMS> {
  prettyName: string;
  formatter?: Formatter;
  // Compute the derived metric from a datum of base-column values. Same function
  // applies per-cell (datum = the row's base cols) and to the totals row (datum =
  // the cross-row sums of base cols). Omit on base columns — they sum directly.
  aggregator?: (datum: AggregatorDatum) => number | null;
  // Base columns required to compute this metric. Used to sum cross-row totals
  // before passing them to aggregator. Omit on base columns — the column's own
  // dataVarName is summed directly.
  requiredColumns?: readonly string[];
  category?: string;
  contentType?: 'date' | 'logo' | 'number' | 'text' | 'thumbnail';
  customMetaData?: CustomMetric;
  decimals?: number;
  name?: DIMS;
  iconStyle?: 'logo' | 'thumbnail' | 'iconOnly';
  isCustom?: boolean;
  minIsBest?: boolean;
  overlayText?: string;
  overlayPlacement?: OverlayTriggerProps['placement'];
  currencyGetter?: (col: Record<string, string | number>) => string;
}

export type ColumnMetaDataMap =
  | Partial<Record<L.ColumnType, ColumnMetaData>>
  | Partial<Record<L.DimensionColumnType, ColumnMetaData>>
  | Partial<Record<S.ColumnType, ColumnMetaData>>
  | Partial<Record<S.PerformanceDimensionColumnType, ColumnMetaData>>
  | Partial<Record<CC.ColumnType, ColumnMetaData>>
  | Partial<Record<CC.DimensionColumnType, ColumnMetaData>>
  | Partial<Record<DimensionColNames, ColumnMetaData>>
  | Partial<Record<DimensionColPrettyNames, ColumnMetaData>>
  | Partial<Record<ColumnOrMetricName, ColumnMetaData>>;

// Determines the footer label MetricsTable renders for a given data column. Priority:
//   1. `dataHeader.footerLabel` (explicit caller override)
//   2. `'AVG'` — meta declares a weighted average (its `requiredColumns` contains
//      `'numRows'` or any string containing `'weighted'` case-insensitively).
//      Forward-compat note: when bpm-frontend's streaming-performance metrics
//      (`weightedAvgFreq`, `weightedMedianFreq`, …) migrate, they should list the
//      weighted base column as a plain string in `requiredColumns` so this branch
//      keeps firing. Object-shape `{ dataVarName, fetchGetter }` no longer exists.
//   3. `'OVERALL'` — meta has a `customMetaData.function_string` whose body uses
//      any operator beyond `+`/`-` (i.e. a real computation), OR meta has a
//      plain `aggregator` (any derived metric).
//   4. `'TOTAL'` — fallback for base columns (sum of values).
export const getFooterType = (
  columnMetaData: ColumnMetaData | undefined,
  dataHeader: Column | undefined
): string => {
  if (dataHeader?.footerLabel) return dataHeader.footerLabel;
  if (columnMetaData?.requiredColumns) {
    for (const column of columnMetaData.requiredColumns) {
      if (column === 'numRows' || column.toLowerCase().includes('weighted')) {
        return 'AVG';
      }
    }
  }
  if (columnMetaData?.customMetaData) {
    for (const operator of [...OPERATORS, ...FUNCTIONS.map(fn => `${fn}(`)]) {
      if (
        columnMetaData.customMetaData.function_string.includes(operator) &&
        operator !== '-' &&
        operator !== '+'
      ) {
        return 'OVERALL';
      }
    }
  }
  if (columnMetaData?.aggregator != null) return 'OVERALL';
  return 'TOTAL';
};

/*
 * Helper for getting segments that appear in both selectedMap and groupedSegments
 */
export const useGroupedSegmentFiltering = (
  filterState: FilterPaneState,
  groupedSegments: Record<string, GroupedSegment[]>
): Record<string, string[]> =>
  useMemo(() => {
    const result: Record<string, string[]> = {};

    if (!filterState.basic.selectedMap || !groupedSegments) {
      return result;
    }

    const { selectedMap } = filterState.basic;

    for (const [groupName, segments] of Object.entries(groupedSegments)) {
      const matchingSegments: string[] = [];

      for (const segment of segments) {
        const { segmentName } = segment;

        if (selectedMap[segmentName] && selectedMap[segmentName].Yes === true) {
          matchingSegments.push(segmentName);
        }
      }

      if (matchingSegments.length > 0) {
        result[groupName] = matchingSegments;
      }
    }

    return result;
  }, [filterState, groupedSegments]);

/*
 * Helpers for comparison metric data
 */
const getRootDataVarName = (rawName: string): string =>
  rawName.replace('_other', '').replace('_delta', '');
const getDelta = (value: number | null, otherValue: number | null) => {
  const showDash =
    !value ||
    !otherValue ||
    Math.abs(value) < 0.00005 ||
    Math.abs(otherValue) < 0.00005 ||
    Math.abs((value - otherValue) / otherValue) > 10000;
  if (showDash) {
    return '-';
  }
  return (value - otherValue) / otherValue;
};

const getIsOtherData = (dataHeader: Column): boolean =>
  Boolean(dataHeader?.dataVarName?.includes('_other'));
const getIsDelta = (dataHeader: Column): boolean =>
  Boolean(dataHeader?.dataVarName?.includes('_delta'));

// Sentinel `CustomMetricKPI.kpi` meaning "whatever KPI is already active for this
// column" — its dataVarName is already present in the column's own fetch bucket,
// no cross-KPI lookup needed. Any other value names a KPI whose data lives in a
// *different* fetch bucket (see `getCrossKpiDatumKey` + the traversal block below).
export const GLOBAL_KPI = 'GLOBAL';

// Key under which a cross-KPI base column's value is stashed on the flat
// `AggregatorDatum` passed to a custom metric's `aggregator`. Deliberately doesn't
// depend on `dataFetchKey`'s output (the lag-suffixed bucket key) so `convertCustomMetric`
// can build the formula's expression string without needing that callback — only the
// traversal block that *populates* this key (which already owns `dataFetchKey`) needs it.
const getCrossKpiDatumKey = (kpiRef: CustomMetricKPI): string =>
  `${kpiRef.kpi}__${kpiRef.dataVarName}`;

const CUSTOM_METRIC_FORMAT_MAP: Record<string, Formatter> = {
  '000s': IMP_IN_THOUSANDS_FORMATTER,
  cpx: CPX_FORMATTER,
  number: toPrettyNumber,
  percent: toPrettyPercent,
  spend: SPEND_OR_REVENUE_FORMATTER
};

// Converts a DB-defined custom metric into a `ColumnMetaData` entry so it can appear
// as a Metrics Table column/dropdown option like any built-in metric. `function_string`
// (e.g. `'fetch.clicks / fetch.impressions'`) is compiled once here into a mathjs
// expression against the metric's own `aggregator(datum)` contract — GLOBAL-kpi
// references read the datum's own dataVarName; cross-KPI references read the flattened
// key the traversal block in `constructTableData`/`computeAggregateData` populates.
export const convertCustomMetric = (metric: CustomMetric): ColumnMetaData | undefined => {
  try {
    // Group KPI overrides by dataVarName, preserving order. A formula can reference the same
    // base field more than once (e.g. `fetch.volume/fetch.volume`), each occurrence bound to a
    // DIFFERENT KPI (e.g. "Leads" vs "Enrollment") — the UI builds `metric.kpis` by mapping over
    // the formula's KPI-token occurrences left-to-right, so its order matches the order those
    // tokens appear in `function_string`. A single global find/replace-by-name can't tell the
    // occurrences apart and collapses them all onto whichever kpiRef for that name is processed
    // first; consuming a per-name queue in formula order binds each occurrence correctly instead.
    const kpiQueueByDataVarName = new Map<string, CustomMetricKPI[]>();
    for (const kpiRef of metric.kpis) {
      const queue = kpiQueueByDataVarName.get(kpiRef.dataVarName) ?? [];
      queue.push(kpiRef);
      kpiQueueByDataVarName.set(kpiRef.dataVarName, queue);
    }
    const expression = metric.function_string.replace(/fetch\.(\w+)/g, (match, dataVarName) => {
      const kpiRef = kpiQueueByDataVarName.get(dataVarName)?.shift();
      if (!kpiRef) return `agg.${dataVarName}`;
      return kpiRef.kpi === GLOBAL_KPI
        ? `agg.${kpiRef.dataVarName}`
        : `agg["${getCrossKpiDatumKey(kpiRef)}"]`;
    });
    const aggregator = (datum: AggregatorDatum): number | null => {
      try {
        const result = evaluate(expression, { agg: datum });
        return typeof result === 'number' && Number.isFinite(result) ? result : null;
      } catch {
        return null;
      }
    };
    return {
      aggregator,
      contentType: 'number',
      customMetaData: metric,
      decimals: metric.decimals,
      formatter: R.isNil(metric.formatter)
        ? CUSTOM_METRIC_FORMAT_MAP.number
        : CUSTOM_METRIC_FORMAT_MAP[metric.formatter],
      isCustom: true,
      minIsBest: metric.min_is_best,
      prettyName: metric.display_name,
      requiredColumns: metric.required_columns
    };
  } catch (e) {
    console.error(`Error converting custom metric "${metric?.name}": ${e}`);
    return undefined;
  }
};

// Look up meta under the *root* dataVarName so comparison columns (`spend_other`,
// `cpc_delta`) share their primary metric's `aggregator`/`requiredColumns`.
export const getColumnMeta = (
  columnMetaDataMap: ColumnMetaDataMap,
  dataHeader: Column
): ColumnMetaData | undefined => {
  if (!columnMetaDataMap || !dataHeader?.dataVarName) return undefined;
  return columnMetaDataMap[getRootDataVarName(dataHeader.dataVarName)];
};

// Base-column names whose values feed the column's `aggregator`. For metrics
// with `requiredColumns`, that's the declared base cols; for base columns, the
// column's own (root) dataVarName.
const getBaseColumnsForTotals = (
  dataHeader: Column,
  meta: ColumnMetaData | undefined
): readonly string[] => meta?.requiredColumns ?? [getRootDataVarName(dataHeader.dataVarName)];

// A custom metric's formula may reference a KPI other than the column's own — those
// values live in a *different* `perfRow.fetches` bucket. For each such reference, read
// it via `dataFetchKey` under the same flattened key `convertCustomMetric`'s compiled
// expression reads from. The key merges the column's own header (lag, audience, etc.)
// with the ref's KPI, so e.g. Single Channel's `${kpi}_${lag}` keeps the column's lag.
// GLOBAL_KPI refs are skipped — their value is already the column's own base column,
// handled by the regular `baseCols` walk at each call site.
const getCrossKpiValues = (
  meta: ColumnMetaData | undefined,
  perfRow: { fetches: Record<string, unknown> },
  dataHeader: Column,
  dataFetchKey: (dataHeader: Column) => string,
  suffix: '' | '_other'
): { flatKey: string; value: string | number | undefined }[] =>
  (meta?.customMetaData?.kpis ?? [])
    .filter(kpiRef => kpiRef.kpi !== GLOBAL_KPI)
    .map(kpiRef => {
      const otherFetchKey = dataFetchKey({ ...dataHeader, kpi: kpiRef.kpi });
      const kpiFetch = (perfRow.fetches[otherFetchKey] ?? {}) as unknown as Record<
        string,
        string | number
      >;
      return {
        flatKey: getCrossKpiDatumKey(kpiRef),
        value: kpiFetch[`${kpiRef.dataVarName}${suffix}`]
      };
    });

// One row's datum for a column's `aggregator`: the row's fetch plus a custom metric's
// cross-KPI values (same keys `constructTableData` builds).
export const getRowAggregatorDatum = (
  meta: ColumnMetaData | undefined,
  perfRow: { fetches: Record<string, unknown> },
  dataHeader: Column,
  dataFetchKey: (dataHeader: Column) => string
): AggregatorDatum => {
  const fetch = perfRow.fetches[dataFetchKey(dataHeader)] as AggregatorDatum;
  const datum: AggregatorDatum = { ...fetch };
  for (const { flatKey, value } of getCrossKpiValues(meta, perfRow, dataHeader, dataFetchKey, '')) {
    datum[flatKey] = value;
  }
  return datum;
};

// Apply the column's `aggregator` to a datum (single-row or summed). For base
// columns without `aggregator`, return the raw value at the root dataVarName.
const evaluateAggregation = (
  meta: ColumnMetaData | undefined,
  datum: AggregatorDatum,
  rootDataVarName: string
): number | string | null => {
  if (meta?.aggregator) return meta.aggregator(datum);
  const raw = datum[rootDataVarName];
  return raw === undefined ? null : raw;
};

// Reduce a stream of per-row currency codes to a single label for the totals row:
// undefined if nothing was reported, the shared code if all rows agreed, or
// `'mixed'` if rows disagreed. Mirrors the old `totalCurrCodeMap` accumulation,
// which tracked four distinct slots independently — `delivery_currency_code`,
// `delivery_currency_code_other`, `performance_currency_code`,
// `performance_currency_code_other` — and let each column's `currencyGetter` pick
// the slot relevant to it (delivery vs. performance, primary vs. comparison).
// Here the per-slot distinction is preserved at the column level: each column's
// own `currencyGetter` reads from whichever fetch key it cares about, and the
// accumulator collapses the per-row stream into one code (or `'mixed'`) for that
// column. Empty / zero results fall back to `'USD'` to match the legacy
// `totalCurrCodeMap` behavior for missing-currency rows.
const accumulateCurrencyCode = (
  acc: string | undefined,
  next: string | undefined
): string | undefined => {
  if (next === undefined) return acc;
  const resolved = next === '' || (next as unknown) === 0 ? 'USD' : next;
  if (acc === undefined) return resolved;
  if (acc === 'mixed' || acc !== resolved) return 'mixed';
  return acc;
};

// Per-row currency codes are read off the row's fetch by the column's
// `currencyGetter`. For `_other` (comparison) columns, the legacy code first
// swapped `_other`-suffixed keys into the base keys so a single `currencyGetter`
// implementation could read e.g. `col.delivery_currency_code` and get the
// comparison row's delivery currency. This helper produces the same view: for
// non-`_other` columns it returns the fetch as-is; for `_other` columns it
// aliases every `*_other` key onto its base name. Delivery vs. performance
// currency-code keys ride through this swap uniformly.
const buildCurrencyView = (
  fetch: Record<string, string | number>,
  isOther: boolean
): Record<string, string | number> => {
  if (!isOther) return fetch;
  const view: Record<string, string | number> = { ...fetch };
  for (const key of Object.keys(fetch)) {
    if (key.endsWith('_other')) {
      view[key.slice(0, -'_other'.length)] = fetch[key];
    }
  }
  return view;
};

export const MISSING_VALUE_LABEL = '--';

// `aggregator` returns `null` to signal a missing value (e.g. denominator-zero in
// a ratio metric). The old `aggregator`/`fetchGetter` callbacks used the string `'--'`
// for the same case — collapse both into a single `'--'` label so downstream sorting
// and exports treat the cell as missing rather than as a literal 0.
export const formatCellLabel = (
  value: number | string | null,
  meta: ColumnMetaData | undefined,
  decimals: number | undefined,
  row?: PerformanceData,
  currCode?: string
): string => {
  if (value === null || value === undefined) return MISSING_VALUE_LABEL;
  if (meta?.formatter) {
    const num = typeof value === 'number' ? value : Number(value);
    return meta.formatter(Number.isFinite(num) ? num : 0, { decimals, row, currCode });
  }
  if (typeof value === 'number' && value === 0) return MISSING_VALUE_LABEL;
  return String(value);
};

const buildGroupedSegmentDimensionEntries = (
  perfRow: PerformanceData,
  groupedSegments: Record<string, GroupedSegment[]>,
  groupedSegmentHeaders: DimensionColumn[],
  getDimensionCell: (
    dimensionData: DimensionMap,
    dimensionHeader: DimensionColumn,
    ...args
  ) => CellData
): Record<string, CellData> => {
  const entries: Record<string, CellData> = {};
  for (const groupedSegmentHeader of groupedSegmentHeaders) {
    const segmentsInGroup = groupedSegments[groupedSegmentHeader.dimensionTypeName];
    const yesSegments: string[] = [];
    let hasNoValues = false;
    for (const segment of segmentsInGroup) {
      const rawValue = (perfRow.dimensions as DimensionMap)[segment.segmentName];
      const tempDimensionHeader: DimensionColumn = {
        id: segment.segmentId?.toString() || '',
        dimensionTypeName: segment.segmentName,
        dimensionVarName: segment.segmentName,
        label: segment.segmentName,
        role: 10,
        divider: false
      };
      const dimensionCell = getDimensionCell(perfRow.dimensions, tempDimensionHeader);
      const processedValue = dimensionCell?.label || dimensionCell?.value || rawValue;
      const processedStr = processedValue == null ? '' : String(processedValue).toLowerCase();
      hasNoValues = hasNoValues || processedStr === 'no';
      if (processedStr === 'yes') {
        yesSegments.push(segment.segmentName);
      }
    }
    const sortedYesSegments = yesSegments.sort((a, b) => a.localeCompare(b));
    if (sortedYesSegments.length > 0) {
      const joined = sortedYesSegments.join(', ');
      entries[groupedSegmentHeader.dimensionTypeName] = { label: joined, value: joined };
    } else if (hasNoValues) {
      entries[groupedSegmentHeader.dimensionTypeName] = { label: 'None', value: 'None' };
    } else {
      entries[groupedSegmentHeader.dimensionTypeName] = { label: 'Unlabeled', value: 'Unlabeled' };
    }
  }
  return entries;
};

export const constructTableData = (
  columnMetaDataMap: ColumnMetaDataMap,
  dataHeaders: Column[],
  dimensionHeaders: DimensionColumn[],
  getDimensionCell: (
    dimensionData: DimensionMap,
    dimensionHeader: DimensionColumn,
    ...args // Can pass in creative map, network map etc.
  ) => CellData,
  dataFetchKey: (dataHeader: Column) => string,
  performanceData: PerformanceData[],
  groupedSegments: Record<string, GroupedSegment[]> = {},
  shouldEnableMetricsTableSegmentGroups = false,
  serverValues?: ServerMetricValues
): {
  aggregateData: CellData[];
  dimensionData: Record<string, CellData>[];
  tableData: CellData[][];
} => {
  // Filter out grouped segments from dimension headers and create grouped segments columns
  const groupedSegmentNames = new Set(
    Object.values(groupedSegments).flatMap(gs => gs.map(g => g.segmentName))
  );
  const groupedSegmentHeaders = dimensionHeaders.filter(
    header =>
      Object.keys(groupedSegments).some(groupName => header.dimensionTypeName === groupName) &&
      shouldEnableMetricsTableSegmentGroups
  );
  // Filter out if dimension is a grouped segment or a segment group
  const nonGroupedDimensionHeaders = shouldEnableMetricsTableSegmentGroups
    ? dimensionHeaders.filter(
        dimHeader =>
          !(
            groupedSegmentNames.has(dimHeader.dimensionTypeName) ||
            Object.keys(groupedSegments).some(
              groupName => dimHeader.dimensionTypeName === groupName
            )
          )
      )
    : dimensionHeaders;

  // Build dimension rows once (independent of metric columns).
  const dimensionData: Record<string, CellData>[] = performanceData.map(perfRow => {
    const dimensionRow: Record<string, CellData> = {};
    for (const dimensionHeader of nonGroupedDimensionHeaders) {
      dimensionRow[dimensionHeader.dimensionTypeName] = {
        ...getDimensionCell(perfRow.dimensions, dimensionHeader)
      };
    }
    Object.assign(
      dimensionRow,
      buildGroupedSegmentDimensionEntries(
        perfRow,
        groupedSegments,
        groupedSegmentHeaders,
        getDimensionCell
      )
    );
    return dimensionRow;
  });

  const tableData: CellData[][] = performanceData.map(() => []);
  const aggregateData: CellData[] = [];

  const rowCount = performanceData.length;

  for (const dataHeader of dataHeaders) {
    // Look up meta under the root dataVarName so `_other` / `_delta` comparison columns
    // share their primary metric's aggregator + formatter.
    const meta = getColumnMeta(columnMetaDataMap, dataHeader);
    const decimals = dataHeader.decimals ?? meta?.decimals;
    const fetchKey = dataFetchKey(dataHeader);
    const baseCols = getBaseColumnsForTotals(dataHeader, meta);
    const rootName = getRootDataVarName(dataHeader.dataVarName);
    const isOther = getIsOtherData(dataHeader);
    const isDelta = getIsDelta(dataHeader);
    // Delta columns swap the formatter to `%∆`; primary/other keep the metric's formatter.
    const effectiveMeta: ColumnMetaData | undefined = isDelta
      ? { ...(meta ?? { prettyName: rootName }), formatter: toPrettyDeltaPercent }
      : meta;
    const primarySuffix = isOther ? '_other' : '';
    const currencyGetter = meta?.currencyGetter;
    const canUseServer = serverValues !== undefined && isServerValueUsable(meta, rootName);
    // Base columns are already server values; only derived cells need the SQL value.
    const readsServerCells = canUseServer && meta?.aggregator !== undefined;

    // For `_delta` columns, totals = delta(sum(primary), sum(other)), not
    // sum(per-row deltas).
    const primarySums: Record<string, number> = {};
    const otherSums: Record<string, number> | undefined = isDelta ? {} : undefined;
    for (const key of baseCols) {
      primarySums[key] = key === 'numRows' ? rowCount : 0;
      if (otherSums) {
        otherSums[key] = key === 'numRows' ? rowCount : 0;
      }
    }

    let aggCurrCode: string | undefined;

    for (let rowIndex = 0; rowIndex < rowCount; rowIndex += 1) {
      const perfRow = performanceData[rowIndex];
      const fetch = (perfRow.fetches[fetchKey] ?? {}) as unknown as Record<string, string | number>;

      let currCode: string | undefined;
      if (currencyGetter) {
        const view = isOther ? buildCurrencyView(fetch, true) : fetch;
        currCode = currencyGetter(view);
        aggCurrCode = accumulateCurrencyCode(aggCurrCode, currCode);
      }

      // Build datum(s) AND accumulate sums in one walk over baseCols.
      const primaryDatum: AggregatorDatum = {};
      const otherDatum: AggregatorDatum | undefined = isDelta ? {} : undefined;
      for (const key of baseCols) {
        if (key === 'numRows') {
          primaryDatum[key] = rowCount;
          if (otherDatum) {
            otherDatum[key] = rowCount;
          }
          continue;
        }
        const pv = fetch[`${key}${primarySuffix}`];
        primaryDatum[key] = pv;
        const pn = Number(pv);
        if (Number.isFinite(pn)) {
          primarySums[key] += pn;
        }
        if (otherDatum && otherSums) {
          const ov = fetch[`${key}_other`];
          otherDatum[key] = ov;
          const on = Number(ov);
          if (Number.isFinite(on)) {
            otherSums[key] += on;
          }
        }
      }

      // Cross-KPI base columns for custom metrics — populated from their own KPI's
      // fetch bucket rather than `fetch`, into the same flat datum the aggregator reads.
      // Must read the same suffix (`primarySuffix`) as the plain-base-column walk above:
      // for a comparison (`_other`) column, `otherDatum`/`otherSums` are never created
      // (only `_delta` columns get those), so this is the only place that column's
      // cross-KPI values get populated — a hardcoded '' here silently fed it primary-
      // period data instead of the comparison period's.
      for (const { flatKey, value } of getCrossKpiValues(
        meta,
        perfRow,
        dataHeader,
        dataFetchKey,
        primarySuffix
      )) {
        primaryDatum[flatKey] = value;
        const num = Number(value);
        if (Number.isFinite(num)) {
          primarySums[flatKey] = (primarySums[flatKey] ?? 0) + num;
        }
      }
      if (otherDatum && otherSums) {
        for (const { flatKey, value } of getCrossKpiValues(
          meta,
          perfRow,
          dataHeader,
          dataFetchKey,
          '_other'
        )) {
          otherDatum[flatKey] = value;
          const num = Number(value);
          if (Number.isFinite(num)) {
            otherSums[flatKey] = (otherSums[flatKey] ?? 0) + num;
          }
        }
      }

      let rowValue: number | string | null;
      if (otherDatum) {
        const primary = evaluateAggregation(meta, primaryDatum, rootName);
        const other = evaluateAggregation(meta, otherDatum, rootName);
        rowValue = getDelta(toFiniteNumberOrNull(primary), toFiniteNumberOrNull(other));
      } else {
        rowValue = evaluateAggregation(meta, primaryDatum, rootName);
      }
      if (readsServerCells) {
        const server = readServerPeriodValue(fetch, rootName, isOther, isDelta);
        if (server.found) {
          rowValue = server.value;
        }
      }

      tableData[rowIndex].push({
        label: formatCellLabel(rowValue, effectiveMeta, decimals, perfRow, currCode),
        value: rowValue ?? MISSING_VALUE_LABEL
      });
    }

    // Totals row — evaluate against accumulated sums.
    let aggregateValue: number | string | null;
    if (otherSums) {
      const primary = evaluateAggregation(meta, primarySums, rootName);
      const other = evaluateAggregation(meta, otherSums, rootName);
      aggregateValue = getDelta(toFiniteNumberOrNull(primary), toFiniteNumberOrNull(other));
    } else {
      aggregateValue = evaluateAggregation(meta, primarySums, rootName);
    }
    if (canUseServer) {
      const server = readServerTotal(serverValues, fetchKey, rootName, isOther, isDelta);
      if (server.found) {
        aggregateValue = server.value;
      }
    }
    aggregateData.push({
      label: formatCellLabel(aggregateValue, effectiveMeta, decimals, undefined, aggCurrCode),
      value: aggregateValue ?? MISSING_VALUE_LABEL
    });
  }

  return { aggregateData, dimensionData, tableData };
};

// The server value for one period of a column: primary, `_other` (joined from the comparison
// request), or the delta of the two.
const readServerPeriodValue = (
  values: Record<string, unknown> | undefined,
  rootName: string,
  isOther: boolean,
  isDelta: boolean,
  otherValues: Record<string, unknown> | undefined = values,
  otherKey = `${rootName}_other`
): { found: boolean; value: number | string | null } => {
  if (!isDelta) return readServerValue(values, isOther ? otherKey : rootName);
  const primary = readServerValue(values, rootName);
  const other = readServerValue(otherValues, otherKey);
  if (!primary.found || !other.found) return { found: false, value: null };
  return { found: true, value: getDelta(primary.value, other.value) };
};

const readServerTotal = (
  serverValues: ServerMetricValues,
  fetchKey: string,
  rootName: string,
  isOther: boolean,
  isDelta: boolean
): { found: boolean; value: number | string | null } => {
  const primary = serverValues.totals?.[fetchKey];
  const other = serverValues.otherTotals?.[fetchKey];
  return readServerPeriodValue(
    isOther ? other : primary,
    rootName,
    false,
    isDelta,
    other,
    rootName
  );
};

const toFiniteNumberOrNull = (value: number | string | null): number | null => {
  if (value === null || value === undefined) return null;
  const num = typeof value === 'number' ? value : Number(value);
  return Number.isFinite(num) ? num : null;
};

// Recompute totals only. Pass `visibleRowIndices` to scope to a subset; omit
// for the full performanceData.
export const computeAggregateData = (
  columnMetaDataMap: ColumnMetaDataMap,
  dataHeaders: Column[],
  dataFetchKey: (dataHeader: Column) => string,
  performanceData: PerformanceData[],
  visibleRowIndices?: ReadonlySet<number>
): CellData[] => {
  const aggregateData: CellData[] = [];
  const rowCount = performanceData.length;
  const visibleCount = visibleRowIndices ? visibleRowIndices.size : rowCount;

  for (const dataHeader of dataHeaders) {
    const meta = getColumnMeta(columnMetaDataMap, dataHeader);
    const decimals = dataHeader.decimals ?? meta?.decimals;
    const fetchKey = dataFetchKey(dataHeader);
    const baseCols = getBaseColumnsForTotals(dataHeader, meta);
    const rootName = getRootDataVarName(dataHeader.dataVarName);
    const isOther = getIsOtherData(dataHeader);
    const isDelta = getIsDelta(dataHeader);
    const effectiveMeta: ColumnMetaData | undefined = isDelta
      ? { ...(meta ?? { prettyName: rootName }), formatter: toPrettyDeltaPercent }
      : meta;
    const primarySuffix = isOther ? '_other' : '';
    const currencyGetter = meta?.currencyGetter;

    const primarySums: Record<string, number> = {};
    const otherSums: Record<string, number> | undefined = isDelta ? {} : undefined;
    for (const key of baseCols) {
      primarySums[key] = key === 'numRows' ? visibleCount : 0;
      if (otherSums) {
        otherSums[key] = key === 'numRows' ? visibleCount : 0;
      }
    }

    let aggCurrCode: string | undefined;

    for (let i = 0; i < rowCount; i += 1) {
      if (visibleRowIndices && !visibleRowIndices.has(i)) continue;
      const perfRow = performanceData[i];
      const fetch = (perfRow.fetches[fetchKey] ?? {}) as unknown as Record<string, string | number>;

      if (currencyGetter) {
        const view = isOther ? buildCurrencyView(fetch, true) : fetch;
        aggCurrCode = accumulateCurrencyCode(aggCurrCode, currencyGetter(view));
      }

      for (const key of baseCols) {
        if (key === 'numRows') continue;
        const pv = fetch[`${key}${primarySuffix}`];
        const pn = Number(pv);
        if (Number.isFinite(pn)) {
          primarySums[key] += pn;
        }
        if (otherSums) {
          const ov = fetch[`${key}_other`];
          const on = Number(ov);
          if (Number.isFinite(on)) {
            otherSums[key] += on;
          }
        }
      }

      // Cross-KPI base columns for custom metrics — same sums, different bucket than `fetch`.
      // Must read `primarySuffix`, not a hardcoded '': for a comparison (`_other`) column,
      // `otherSums` is never created (only `_delta` columns get one), so this is the only
      // place that column's cross-KPI values get populated — hardcoding '' here silently
      // fed it primary-period data instead of the comparison period's.
      for (const { flatKey, value } of getCrossKpiValues(
        meta,
        perfRow,
        dataHeader,
        dataFetchKey,
        primarySuffix
      )) {
        const num = Number(value);
        if (Number.isFinite(num)) {
          primarySums[flatKey] = (primarySums[flatKey] ?? 0) + num;
        }
      }
      if (otherSums) {
        for (const { flatKey, value } of getCrossKpiValues(
          meta,
          perfRow,
          dataHeader,
          dataFetchKey,
          '_other'
        )) {
          const num = Number(value);
          if (Number.isFinite(num)) {
            otherSums[flatKey] = (otherSums[flatKey] ?? 0) + num;
          }
        }
      }
    }

    let aggregateValue: number | string | null;
    if (otherSums) {
      const primary = evaluateAggregation(meta, primarySums, rootName);
      const other = evaluateAggregation(meta, otherSums, rootName);
      aggregateValue = getDelta(toFiniteNumberOrNull(primary), toFiniteNumberOrNull(other));
    } else {
      aggregateValue = evaluateAggregation(meta, primarySums, rootName);
    }
    aggregateData.push({
      label: formatCellLabel(aggregateValue, effectiveMeta, decimals, undefined, aggCurrCode),
      value: aggregateValue ?? MISSING_VALUE_LABEL
    });
  }

  return aggregateData;
};

export interface SortDataResult {
  sortedDimensionData: Record<string, CellData>[];
  sortedTableData: CellData[][];
  // Display-index -> original-index permutation. Lets downstream consumers
  // reuse per-row work computed against the unsorted data without re-running
  // it on every sort.
  sortedIndices: number[];
}

// Replaces `'--'` placeholders with 0 so they sort like numeric zeros instead
// of throwing off > / < comparisons against real numbers.
const sortValue = (v: CellData['value']) => (v === '--' ? 0 : v);

// Returns a cell-wise normalized copy where '--' values are turned into 0,
// but only allocates new cell objects for cells that actually need it. The
// row itself is only re-allocated when at least one cell changed.
const normalizeRowForOutput = (row: CellData[]): CellData[] => {
  let hasMissing = false;
  for (const cell of row) {
    if (cell.value === '--') {
      hasMissing = true;
      break;
    }
  }
  if (!hasMissing) return row;
  return row.map(cell => (cell.value === '--' ? { ...cell, value: 0 } : cell));
};

export const sortData = (
  dataHeaders: Column[],
  dimensionData: Record<string, CellData>[],
  dimensionDataHeaders: DimensionColumn[],
  sorting: SortInfo[],
  tableData: CellData[][],
  hasDimensionData: boolean,
  // Filters the sorted index permutation. Pass `undefined` (not a full-row-count set)
  // when no filter is active so the fast path below activates.
  visibleRowIndices?: ReadonlySet<number>
): SortDataResult => {
  const n = tableData.length;
  const hasSort = sorting != null && sorting.length > 0;
  const hasMask = visibleRowIndices != null && visibleRowIndices.size < n;

  // Fast path: no sort, no mask — return inputs as-is, avoiding the
  // normalizeRowForOutput walk on every commit.
  if (!hasSort && !hasMask) {
    return {
      sortedDimensionData: dimensionData,
      sortedTableData: tableData,
      sortedIndices: Array.from({ length: n }, (_, i) => i)
    };
  }

  const identityIndices = Array.from({ length: n }, (_, i) => i);

  const sortedAll = (() => {
    if (!hasSort) return identityIndices;

    // Build index-based comparators. Each takes two original-row indices and
    // looks up its values directly from `tableData` / `dimensionData` instead
    // of pre-cloning a normalized table.
    const comparators: ((a: number, b: number) => number)[] = [];
    for (const { id, asc } of sorting) {
      const dimensionCol = dimensionDataHeaders.find(col => col.id === id);
      if (dimensionCol) {
        const key = dimensionCol.dimensionTypeName;
        comparators.push((a, b) => {
          const va = dimensionData[a]?.[key]?.value;
          const vb = dimensionData[b]?.[key]?.value;
          const cmp = (va > vb ? 1 : 0) - (va < vb ? 1 : 0);
          return asc ? cmp : -cmp;
        });
        continue;
      }
      const found = dataHeaders.findIndex(col => col.id === id);
      const idx = found === -1 ? 0 : found;
      if (!dataHeaders[idx]) continue;
      comparators.push((a, b) => {
        const va = sortValue(tableData[a][idx].value);
        const vb = sortValue(tableData[b][idx].value);
        const cmp = (va > vb ? 1 : 0) - (va < vb ? 1 : 0);
        return asc ? cmp : -cmp;
      });
    }

    return identityIndices.sort((a, b) => {
      for (const comparator of comparators) {
        const result = comparator(a, b);
        if (result !== 0) return result;
      }
      return 0;
    });
  })();

  const displayedIndices = hasMask ? sortedAll.filter(i => visibleRowIndices.has(i)) : sortedAll;

  const sortedDimensionData = hasDimensionData
    ? displayedIndices.map(i => dimensionData[i])
    : dimensionData;
  const sortedTableData = displayedIndices.map(i => normalizeRowForOutput(tableData[i]));

  return { sortedDimensionData, sortedTableData, sortedIndices: displayedIndices };
};

const isHeatMappableValue = (value: unknown, excludeZeros: boolean | undefined): value is number =>
  hasNumber(value) && !(excludeZeros && value === 0);

export const getHeatMap = (
  dataHeaders: Column[],
  tableData: CellData[][]
): (HeatMapData | null)[] => {
  const heatMap: (HeatMapData | null)[] = [];
  for (let colIndex = 0; colIndex < dataHeaders.length; ++colIndex) {
    const column = dataHeaders[colIndex];
    if (column.heatMapping) {
      const values: number[] = [];
      for (const row of tableData) {
        const val = row[colIndex].value;
        if (isHeatMappableValue(val, column.heatMapping.excludeZeros)) {
          values.push(val);
        }
      }
      let max = -Infinity;
      let min = Infinity;
      let sum = 0;
      for (const val of values) {
        if (val < min) {
          min = val;
        }
        if (val > max) {
          max = val;
        }
        sum += val;
      }
      // If the config minimum is bigger than the actual minimum, use that one
      if (column.heatMapping.min != null && column.heatMapping.min > min) {
        ({ min } = column.heatMapping);
      }
      // If the config maximum is smaller than the actual maximum, use that one
      if (column.heatMapping.max != null && column.heatMapping.max < max) {
        ({ max } = column.heatMapping);
      }
      const mean = sum / values.length;
      const stdClip =
        column.heatMapping.stdevClipping == null ? 1 : column.heatMapping.stdevClipping;
      if (stdClip > 0) {
        let stdevCalc = 0;
        for (const val of values) {
          stdevCalc += (val - mean) ** 2;
        }
        const stdev = Math.sqrt(stdevCalc / values.length);
        min = Math.max(min, mean - stdev * stdClip);
        max = Math.min(max, mean + stdev * stdClip);
      }
      let midpoint: number;
      switch (column.heatMapping.midpoint) {
        case 'MEDIAN':
          midpoint = values.length
            ? [...values].sort((a, b) => a - b)[Math.floor(values.length / 2)]
            : 0;
          break;
        case 'MEAN':
          midpoint = mean;
          break;
        case 'CUSTOM':
          midpoint = column.heatMapping?.customMidpoint || 0;
          break;
        default:
          midpoint = (max - min) / 2 + min;
          break;
      }
      heatMap.push({
        max,
        midpoint,
        min
      });
    } else {
      heatMap.push(null);
    }
  }
  return heatMap;
};

export interface CellDataHeatMapInfo {
  backgroundColor: string;
  outline: string;
}

// Unset, cost metrics such as CPX, CPC and CPM default to min is best via the bpm-types registry.
export const resolveMinIsBest = (
  dataVarName: string,
  heatMapping: Column['heatMapping'],
  meta: ColumnMetaData | undefined
): boolean =>
  heatMapping?.minIsBest ??
  meta?.minIsBest ??
  !resolveHigherIsBetter(getRootDataVarName(dataVarName));

export const getHeatMapInfo = (
  columnIndex: number,
  columnMetaDataMap: ColumnMetaDataMap,
  dataHeaders: Column[],
  heatMap: (HeatMapData | null)[],
  value: number
): CellDataHeatMapInfo => {
  const heatMapInfo = heatMap[columnIndex];
  const col = dataHeaders[columnIndex];
  const colHeatMapMetaData = col.heatMapping ?? {
    colorScheme: 'sequential',
    excludeZeros: false
  };
  const colMetaDataMap = columnMetaDataMap[col.dataVarName];
  if (!(heatMap && colHeatMapMetaData && heatMapInfo)) {
    return { backgroundColor: 'transparent', outline: 'none' };
  }
  const { min, max, midpoint } = heatMapInfo;
  const { colorScheme, excludeZeros } = colHeatMapMetaData;
  const contentReplacement = colMetaDataMap?.contentReplacement;
  const minIsBest = resolveMinIsBest(col.dataVarName, col.heatMapping, colMetaDataMap);
  if (
    min === max ||
    (excludeZeros && value === 0) ||
    (contentReplacement && value <= contentReplacement.threshold)
  ) {
    return { backgroundColor: 'transparent', outline: 'none' };
  }
  if (colorScheme && colorScheme === 'diverging') {
    const backgroundColor = getSteppedHeatColor(
      {
        min,
        max,
        midpoint,
        stops: minIsBest ? DIVERGENT_STOPS_MIN_IS_BEST : DivergentColorMap20
      },
      value
    );
    // The ramp's innermost stops are very pale, so a cell sitting essentially on the midpoint gets
    // a hairline to keep it distinguishable from a cell that is not heat-mapped at all.
    const arm = value > midpoint ? max - midpoint : midpoint - min;
    const proximity = arm > 0 ? Math.min(1, Math.abs(value - midpoint) / arm) : 1;
    return {
      backgroundColor,
      outline: proximity < 0.05 ? `1px solid ${DivergentGreenDark}` : 'none'
    };
  }
  if (colorScheme && colorScheme === 'sequential') {
    // No `midpoint`: a magnitude scale spans the domain in a single arm.
    return {
      backgroundColor: getSteppedHeatColor(
        { min, max, stops: minIsBest ? SEQUENTIAL_STOPS_MIN_IS_BEST : SequentialGreenRamp },
        value
      ),
      outline: 'none'
    };
  }
  return {
    backgroundColor: 'none',
    outline: 'none'
  };
};

interface SpanHeader {
  start: number;
  end: number;
}

export const adjustSupersAfterRemoval = <T extends SpanHeader>(headers: T[], i: number): T[] =>
  headers.reduce<T[]>((acc, header) => {
    // If it's a one-column-wide header and that column is the one we're deleting, delete
    // the header
    if (header.start === header.end && header.start === i) {
      return acc;
    }
    // If the header contains the element, then the end position shifts down, but the
    // start position remains the same.
    if (header.start <= i && header.end >= i) {
      return [...acc, { ...header, end: header.end - 1 }];
    }
    // If the header is before this column, add it in and continue
    if (header.end < i) {
      return [...acc, header];
    }
    // At this point we know the header doesn't contain the column and isn't before the
    // column. Thus, it must be after. That means the start and end need to be shifted
    // down because a column before it was removed.
    return [...acc, { ...header, start: header.start - 1, end: header.end - 1 }];
  }, []);

export const useSortAdder = (
  setSorting: Dispatch<SetStateAction<SortInfo[]>>,
  disableMulti = false
): ((id: string) => void) =>
  useCallback(
    (id: string) => {
      //  If a user selects  an already-sorted column, it flips it if it's descending, and removes it otherwise.
      setSorting(sorting => {
        let newSorting: SortInfo[] = [];
        let sawSelf = false;
        for (const sortItem of sorting) {
          if (sortItem.id === id) {
            sawSelf = true;
            // If it's descending, then flip. Otherwise, skip.
            if (!sortItem.asc) {
              newSorting.push({ id, asc: true });
            }
          } else {
            newSorting.push(sortItem);
          }
        }
        if (!sawSelf) {
          if (disableMulti) {
            // If we pick an entirely new column to sort, we want to clear out
            // all the current sorting.
            newSorting = [{ id, asc: false }];
          } else {
            newSorting.push({ id, asc: false });
          }
        }
        return newSorting;
      });
    },
    [disableMulti, setSorting]
  );

export const areFilterStatesEqual = (
  state1: FilterPaneState | null | undefined,
  state2: FilterPaneState | null | undefined
): boolean => {
  if (state1 == null || state2 == null) {
    // If one of the state is undefined, we can check that they are both undefined or not
    return state1 === state2;
  }
  if (state1.isAdvanced !== state2.isAdvanced) {
    return false;
  }
  if (state1.isAdvanced) {
    return state1.advanced === state2.advanced;
  }
  const notMap1 = state1.basic.notMap;
  const notMap2 = state2.basic.notMap;
  if (Object.keys(notMap1).length !== Object.keys(notMap2).length) {
    return false;
  }
  const notMap1Keys = Object.keys(notMap1).sort();
  const notMap2Keys = Object.keys(notMap2).sort();
  for (let i = 0; i < notMap1Keys.length; i++) {
    const key1 = notMap1Keys[i];
    const key2 = notMap2Keys[i];
    if (key1 !== key2 || notMap1[key1] !== notMap2[key2]) {
      return false;
    }
  }
  const selectedMap1 = state1.basic.selectedMap;
  const selectedMap2 = state2.basic.selectedMap;
  if (Object.keys(selectedMap1).length !== Object.keys(selectedMap2).length) {
    return false;
  }
  const selectedMap1Keys = Object.keys(selectedMap1).sort();
  const selectedMap2Keys = Object.keys(selectedMap2).sort();
  for (let i = 0; i < selectedMap1Keys.length; i++) {
    const key1 = selectedMap1Keys[i];
    const key2 = selectedMap2Keys[i];
    if (key1 !== key2) {
      return false;
    }
    const dimensionMap1 = selectedMap1[key1];
    const dimensionMap2 = selectedMap2[key2];
    if (Object.keys(dimensionMap1).length !== Object.keys(dimensionMap2).length) {
      return false;
    }
    const dimensionMap1Keys = Object.keys(dimensionMap1).sort();
    const dimensionMap2Keys = Object.keys(dimensionMap2).sort();
    for (let j = 0; j < dimensionMap1Keys.length; j++) {
      const dimKey1 = dimensionMap1Keys[j];
      const dimKey2 = dimensionMap2Keys[j];
      if (dimKey1 !== dimKey2 || dimensionMap1[dimKey1] !== dimensionMap2[dimKey2]) {
        return false;
      }
    }
  }

  return true;
};

export const getDataHeaders = (preset: Partial<MetricsTablePreset>, role: number): Column[] => {
  const dataColumns = preset && preset.dataColumns ? preset.dataColumns : [];
  return dataColumns.filter(col => (col && col.role ? col.role >= role : true));
};

export const getDimensionDataHeaders = (
  preset: Partial<MetricsTablePreset>,
  role: number,
  groupedSegments: Record<string, GroupedSegment[]> = {},
  shouldEnableMetricsTableSegmentGroups = false
): DimensionColumn[] => {
  const dimensionColumns = preset && preset.dimensionColumns ? preset.dimensionColumns : [];
  const filteredColumns = dimensionColumns.filter(col =>
    col && col.role ? col.role >= role : true
  );

  if (!shouldEnableMetricsTableSegmentGroups) {
    return filteredColumns;
  }

  const groupedSegmentNames = Object.values(groupedSegments).flatMap(gs =>
    gs.map(g => g.segmentName)
  );
  const nonGroupedColumns = filteredColumns.filter(
    col => !groupedSegmentNames.some(bs => bs === col.dimensionTypeName)
  );

  nonGroupedColumns.map(col => {
    if (
      Object.keys(groupedSegments).some(groupName => col.dimensionTypeName === groupName) &&
      shouldEnableMetricsTableSegmentGroups
    ) {
      return {
        id: `grouped-segments-${col.dimensionTypeName.toLowerCase().replace(' ', '-')}`,
        dimensionTypeName: col.dimensionTypeName,
        dimensionVarName: col.dimensionVarName,
        label: col.dimensionVarName,
        role: 10,
        divider: false
      };
    }
    return col;
  });

  return nonGroupedColumns;
};

const COMPARISON_SUB_HEADER_OPTIONS = new Set(['primary', 'comparison', '%∆']);

export const getExportedData = ({
  columnMetaDataMap,
  dataHeaders,
  dimensionDataHeaders,
  numDataCols,
  numDimCols,
  sortedDimensionData,
  sortedTableData
}: {
  columnMetaDataMap: ColumnMetaDataMap;
  dataHeaders: Column[];
  dimensionDataHeaders: DimensionColumn[];
  numDataCols: number;
  numDimCols: number;
  sortedDimensionData: Record<string, CellData>[];
  sortedTableData: CellData[][];
}): (string | number)[][] => {
  const exportedData: (string | number)[][] = [];
  for (let rowIndex = 0; rowIndex <= sortedDimensionData.length; rowIndex++) {
    const row: (string | number)[] = [];
    for (let leftColIndex = 0; leftColIndex < numDimCols; leftColIndex++) {
      if (rowIndex === 0) {
        const dimensionTypeName = dimensionDataHeaders[leftColIndex]?.dimensionTypeName ?? '';
        const label = dimensionDataHeaders[leftColIndex]?.label ?? null;
        const prettyName =
          (columnMetaDataMap as Record<string, ColumnMetaData>)[dimensionTypeName]?.prettyName ??
          '';
        row.push(label ?? prettyName);
      } else {
        row.push(
          sortedDimensionData[rowIndex - 1][dimensionDataHeaders[leftColIndex].dimensionTypeName]
            .value
        );
      }
    }
    for (let colIndex = 0; colIndex < numDataCols; colIndex++) {
      if (rowIndex === 0) {
        const dataHeaderLabel = dataHeaders[colIndex].label;
        const colPrettyName =
          columnMetaDataMap[getRootDataVarName(dataHeaders[colIndex].dataVarName)]?.prettyName;
        if (
          dataHeaderLabel &&
          colPrettyName &&
          COMPARISON_SUB_HEADER_OPTIONS.has(dataHeaderLabel)
        ) {
          row.push(`${colPrettyName} ${dataHeaderLabel}`);
        } else {
          row.push(dataHeaderLabel ?? colPrettyName);
        }
      } else {
        row.push(sortedTableData[rowIndex - 1][colIndex].value);
      }
    }
    exportedData.push(row);
  }

  return exportedData;
};

// Relocated to utils/text-utils so generic components can use it without depending
// on MetricsTable; re-exported here for existing consumers.
export { transformEmailToFullName } from '../../utils/text-utils';

let sharedTextWidthCanvas: HTMLCanvasElement | null = null;

// Soft cap on consumer-provided caches. Sized to comfortably hold one XXL
// dataset (~280k entries) plus a real production table, while bounding worst-
// case memory to ~75 MB before the wipe-and-continue triggers.
export const TEXT_WIDTH_CACHE_SOFT_CAP = 500_000;

/** Px of slack added to a measured text width. */
export const TEXT_WIDTH_MARGIN = 4;

// Canvas measurement is pure for a fixed (text, font) pair and these widths
// feed a `numRows * numCols` layout loop in MetricsTable. Callers pass an
// instance-scoped cache so memoization is bounded by component lifetime and
// the soft cap prevents unbounded growth across many filter/refetch cycles.
export const getTextWidth = (
  text: string,
  fontSize = 14, // Defaults here are for cross/single channel page dimension cells
  fontWeight = 600,
  font = 'Geist',
  cache?: Map<string, number>
): number => {
  const cacheKey = cache ? `${fontWeight}|${fontSize}|${font}|${text}` : '';
  if (cache) {
    const cached = cache.get(cacheKey);
    if (cached !== undefined) return cached;
  }

  if (!sharedTextWidthCanvas) {
    sharedTextWidthCanvas = document.createElement('canvas');
  }
  const context = sharedTextWidthCanvas.getContext('2d');
  if (!context) return -1;

  context.font = `${fontWeight} ${fontSize}px ${font}`;
  const { width } = context.measureText(text);
  if (cache) {
    if (cache.size >= TEXT_WIDTH_CACHE_SOFT_CAP) {
      cache.clear();
    }
    cache.set(cacheKey, width);
  }
  return width;
};

export interface HeaderTextHeights {
  oneLine: number;
  twoLine: number;
  threeLine: number;
}

/**
 * Measure a header label and return its single-line width plus the row height it needs.
 *
 * `columnWidth` is the width the label wraps at, and differs per header type — dimension headers use
 * the dimension max width, data headers the data-column max width.
 */
export const measureHeaderLabel = ({
  label,
  columnWidth,
  allowMultiLine,
  heights,
  textWidthCache,
  measureText = (text: string) => getTextWidth(text, 14, 501, 'Geist', textWidthCache)
}: {
  label: string;
  columnWidth: number;
  allowMultiLine: boolean;
  heights: HeaderTextHeights;
  textWidthCache?: Map<string, number>;
  /** Override the measurement. `getTextWidth` returns -1 without a canvas 2d context. */
  measureText?: (text: string) => number;
}): { labelWidth: number; headerHeight: number } => {
  const labelWidth = Math.ceil(measureText(label)) + TEXT_WIDTH_MARGIN;
  const numLines = Math.ceil(labelWidth / columnWidth);
  if (!allowMultiLine) {
    return { labelWidth, headerHeight: heights.oneLine };
  }
  if (numLines > 2) {
    return { labelWidth, headerHeight: heights.threeLine };
  }
  if (numLines > 1) {
    return { labelWidth, headerHeight: heights.twoLine };
  }
  return { labelWidth, headerHeight: heights.oneLine };
};

const normalizeDimValue = (value: unknown) => (value === '' ? null : (value ?? null));

export const joinMetricsTableData = (
  data: PerformanceDataRow[],
  otherData: PerformanceDataRow[],
  start: string | undefined,
  end: string | undefined,
  otherStart: string | undefined,
  otherEnd: string | undefined,
  tablePreset: MetricsTablePreset | undefined
): PerformanceDataRow[] => {
  const result = [] as PerformanceDataRow[];
  let dateGrouping = 'day';
  if (tablePreset && tablePreset.dimensionColumns) {
    for (const dimCol of tablePreset.dimensionColumns) {
      if (dimCol.dateGrouping) {
        ({ dateGrouping } = dimCol);
        break;
      }
    }
  }
  const toDate = (d: string) => {
    const cleanDate = d.split('T')[0];
    return new Date(`${cleanDate}T00:00:00`);
  };
  // A row's Date is not always a date: the page fills a missing Date with '' and the weekly
  // bucketing emits 'Unknown' for days outside every bucket. Formatting either throws, so such
  // rows skip the comparison-date shift.
  const parseRowDate = (row: PerformanceDataRow) => {
    const { Date: rowDateStr } = row.dimensions;
    const parsed = rowDateStr == null ? null : toDate(rowDateStr);
    return parsed && Dfns.isValid(parsed) ? parsed : null;
  };
  // Chronological, with rows that have no usable Date last (a NaN comparator result would leave
  // their position up to the engine).
  const byDate = (a: PerformanceDataRow, b: PerformanceDataRow) => {
    const dateA = parseRowDate(a);
    const dateB = parseRowDate(b);
    if (!dateA || !dateB) {
      return Number(!dateA) - Number(!dateB);
    }
    return dateA.getTime() - dateB.getTime();
  };
  const sortedData = R.clone(data).sort(byDate);
  const sortedOtherData = R.clone(otherData).sort(byDate);
  const startDate = start ? toDate(start) : null;
  const otherStartDate = otherStart ? toDate(otherStart) : null;
  // Both periods are needed to shift a row; computed once for the two maps below.
  const shiftBounds =
    startDate && otherStartDate && end != null && otherEnd != null
      ? { startDate, otherStartDate }
      : null;
  const workingData = sortedData.map(row => {
    const rowDate = parseRowDate(row);
    if (!rowDate || !shiftBounds) {
      return row;
    }
    const { startDate: currentStart, otherStartDate: previousStart } = shiftBounds;
    let targetDate: Date;
    if (dateGrouping === 'month') {
      const monthDiff = Dfns.differenceInCalendarMonths(previousStart)(currentStart);
      targetDate = Dfns.subMonths(monthDiff)(rowDate);
    } else if (dateGrouping === 'week') {
      const weekDiff = Dfns.differenceInCalendarWeeks(previousStart)(currentStart);
      targetDate = Dfns.subWeeks(weekDiff)(rowDate);
    } else if (dateGrouping === 'quarter') {
      const quarterDiff = Dfns.differenceInCalendarQuarters(previousStart)(currentStart);
      targetDate = Dfns.subQuarters(quarterDiff)(rowDate);
    } else if (dateGrouping === 'year') {
      const yearDiff = Dfns.differenceInCalendarYears(previousStart)(currentStart);
      targetDate = Dfns.subYears(yearDiff)(rowDate);
    } else {
      const dayDiff = Dfns.differenceInCalendarDays(previousStart)(currentStart);
      targetDate = Dfns.subDays(dayDiff)(rowDate);
    }
    return {
      ...row,
      dimensions: {
        ...row.dimensions,
        ComparisonDate: Dfns.format(DATE_FORMAT)(targetDate)
      }
    };
  });
  const otherWorkingData: (PerformanceDataRow & { addedToJoinedData?: boolean })[] =
    sortedOtherData.map(row => {
      const rowDate = parseRowDate(row);
      if (!rowDate || !shiftBounds) {
        return row;
      }
      const { startDate: currentStart, otherStartDate: previousStart } = shiftBounds;
      let targetDate: Date;
      if (dateGrouping === 'month') {
        const monthDiff = Dfns.differenceInCalendarMonths(previousStart)(currentStart);
        targetDate = Dfns.addMonths(monthDiff)(rowDate);
      } else if (dateGrouping === 'week') {
        const weekDiff = Dfns.differenceInCalendarWeeks(previousStart)(currentStart);
        targetDate = Dfns.addWeeks(weekDiff)(rowDate);
      } else if (dateGrouping === 'quarter') {
        const quarterDiff = Dfns.differenceInCalendarQuarters(previousStart)(currentStart);
        targetDate = Dfns.addQuarters(quarterDiff)(rowDate);
      } else if (dateGrouping === 'year') {
        const yearDiff = Dfns.differenceInCalendarYears(previousStart)(currentStart);
        targetDate = Dfns.addYears(yearDiff)(rowDate);
      } else {
        const dayDiff = Dfns.differenceInCalendarDays(previousStart)(currentStart);
        targetDate = Dfns.addDays(dayDiff)(rowDate);
      }
      return {
        ...row,
        dimensions: {
          ...row.dimensions,
          ComparisonDate: row.dimensions.Date,
          Date: Dfns.format(DATE_FORMAT)(targetDate)
        }
      };
    });
  for (const item of workingData) {
    const currDims = item.dimensions;
    const newItem = { dimensions: { ...currDims }, fetches: {} };
    const matchingOtherData = otherWorkingData.find(otherItem => {
      if (otherItem.addedToJoinedData) {
        return false;
      }
      const otherDims = otherItem.dimensions;
      // The lambda omits null dimensions, so a blank value is a missing key. Compare the union of
      // both rows' keys, with missing/null/'' equal, or a blank row matches any row sharing its
      // remaining dimensions.
      const dimKeys = new Set([...Object.keys(currDims), ...Object.keys(otherDims)]);
      for (const dimKey of dimKeys) {
        if (normalizeDimValue(currDims[dimKey]) !== normalizeDimValue(otherDims[dimKey])) {
          return false;
        }
      }
      // eslint-disable-next-line no-param-reassign
      otherItem.addedToJoinedData = true;
      return true;
    });
    for (const fetchKey of Object.keys(item.fetches)) {
      newItem.fetches[fetchKey] = { ...item.fetches[fetchKey] };
      for (const col of Object.keys(item.fetches[fetchKey])) {
        newItem.fetches[fetchKey][`${col}_other`] =
          matchingOtherData && matchingOtherData.fetches[fetchKey]
            ? matchingOtherData.fetches[fetchKey][col]
            : null;
        const val = newItem.fetches[fetchKey][col] || 0;
        const otherVal = newItem.fetches[fetchKey][`${col}_other`] || 0;
        newItem.fetches[fetchKey][`${col}_delta`] = getDelta(val, otherVal);
      }
    }
    result.push(newItem);
  }
  const remainingOtherData = otherWorkingData.filter(item => !item.addedToJoinedData);
  for (const item of remainingOtherData) {
    const currDims = item.dimensions;
    const newItem = { dimensions: { ...currDims }, fetches: {} };
    for (const fetchKey of Object.keys(item.fetches)) {
      newItem.fetches[fetchKey] = { ...item.fetches[fetchKey] };
      for (const col of Object.keys(item.fetches[fetchKey])) {
        newItem.fetches[fetchKey][`${col}_other`] = newItem.fetches[fetchKey][col];
        newItem.fetches[fetchKey][col] = null;
        newItem.fetches[fetchKey][`${col}_delta`] = getDelta(null, null);
      }
    }
    result.push(newItem);
  }
  return result;
};

export const convertKPIMapToOptions = (
  prefix: string,
  kpiMap: Record<string, KpiInfo> = {}
): { label: string; value: string; trailingContent?: ReactElement }[] => {
  const options: { label: string; value: string; trailingContent?: ReactElement }[] = [];
  if (kpiMap == null) {
    return options;
  }
  for (const kpi of Object.keys(kpiMap)) {
    const kpiInfo = kpiMap[kpi];
    const option: { label: string; value: string; trailingContent?: ReactElement } = {
      label: kpiInfo.name,
      value: kpiInfo.name
    };
    const uniqKeys = Object.keys(kpiInfo).filter(
      key => key !== 'id' && key !== 'name' && kpiInfo[key] != null && kpiInfo[key].length > 0
    );
    if (uniqKeys.length) {
      option.label = `${kpiInfo.name} (${uniqKeys.map(key => kpiInfo[key]).join(', ')})`;
      option.value = kpi;
    } else if (prefix === COMMERCE) {
      option.label = kpiInfo.name;
      option.value = kpi;
    }
    options.push(option);
  }
  return [...options].sort((a, b) => (a.label > b.label ? 1 : 0) - (a.label < b.label ? 1 : 0));
};

export const createComparisonTablePreset = (preset: MetricsTablePreset): MetricsTablePreset => {
  const compPreset = R.clone(preset);
  const newSuperHeaders: SuperHeader[] = compPreset.dataColumns
    ? compPreset.dataColumns.map(col => ({
        data: {
          text: col.label ? col.label : col.dataVarName,
          leftDivider: true,
          rightDivider: true
        },
        span: 3
      }))
    : [];
  const newDataCols = compPreset.dataColumns
    ? compPreset.dataColumns.reduce((newCols, col) => {
        newCols.push(
          { ...col, label: 'primary' },
          { ...col, id: uuid.v4(), dataVarName: `${col.dataVarName}_other`, label: 'comparison' },
          {
            ...col,
            id: uuid.v4(),
            dataVarName: `${col.dataVarName}_delta`,
            label: '%∆',
            decimals: 0
          }
        );
        return newCols;
      }, [] as Column[])
    : [];
  const newDimCols = compPreset.dimensionColumns
    ? compPreset.dimensionColumns.reduce((newCols, col) => {
        newCols.push(col);
        if (col.dimensionVarName === 'Date') {
          newCols.push({
            ...col,
            dimensionTypeName: `Comparison${col.dimensionTypeName}`,
            dimensionVarName: `Comparison${col.dimensionVarName}`,
            id: uuid.v4(),
            label: 'Comparison Date'
          });
        }
        return newCols;
      }, [] as DimensionColumn[])
    : [];
  compPreset.dataColumns = newDataCols;
  compPreset.dimensionColumns = newDimCols;
  compPreset.superHeaders = newSuperHeaders;
  return compPreset;
};
